import React, { useState, useEffect } from "react";
import {
  Container,
  Form,
  Button,
  Alert,
  Card,
  Table,
  Spinner,
} from "react-bootstrap";
import api from "../api";

export default function AddUser() {
  const [form, setForm] = useState({
    username: "",
    password: "",
    role: "VA",
  });

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const getPermissions = (role) => {
    switch (role) {
      case "OWNER":
        return "Full access to everything, including users and accounting.";

      case "MANAGER":
        return "Full operational access. User management is OWNER only.";

      case "LISTER":
        return "Full operational access except Accounting, Budget, Profit Calculator, and restricted financial reports.";

      case "VA":
        return "Quick Buy, Profit Calculator, FIFO batch release, and Shopify import.";

      case "ITEM_LOOKUP":
        return "Read-only access to product and bin lookup.";

      default:
        return "Unknown permissions.";
    }
  };

  // Fetch current users
  const fetchUsers = async () => {
    try {
      setLoading(true);

      const res = await api.get("/users");

      setUsers(res.data);
    } catch (err) {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage("");
    setError("");

    try {
      const res = await api.post("/users", form);

      setMessage(res.data.message);

      setForm({
        username: "",
        password: "",
        role: "VA",
      });

      fetchUsers();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to create user"
      );
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user?"
      )
    ) {
      return;
    }

    try {
      await api.delete(`/users/${id}`);

      setMessage("User deleted successfully");

      fetchUsers();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to delete user"
      );
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await api.patch(`/users/${id}`, updates);

      setMessage("User updated successfully");

      fetchUsers();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to update user"
      );
    }
  };

  return (
    <Container
      style={{
        maxWidth: "1100px",
        marginTop: "2rem",
      }}
    >
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <h3 className="mb-4 text-center">
            ➕ Add New User
          </h3>

          {message && (
            <Alert variant="success">
              {message}
            </Alert>
          )}

          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group
              controlId="username"
              className="mb-3"
            >
              <Form.Label>
                Username
              </Form.Label>

              <Form.Control
                type="text"
                name="username"
                placeholder="Enter username"
                value={form.username}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group
              controlId="password"
              className="mb-3"
            >
              <Form.Label>
                Password
              </Form.Label>

              <Form.Control
                type="password"
                name="password"
                placeholder="Enter password"
                value={form.password}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group
              controlId="role"
              className="mb-3"
            >
              <Form.Label>
                Role
              </Form.Label>

              <Form.Select
                name="role"
                value={form.role}
                onChange={handleChange}
              >
                <option value="VA">
                  VA
                </option>

                <option value="ITEM_LOOKUP">
                  Item Lookup
                </option>

                <option value="LISTER">
                  Lister
                </option>

                <option value="MANAGER">
                  Manager
                </option>

                <option value="OWNER">
                  Owner
                </option>
              </Form.Select>

              <div
                style={{
                  marginTop: "8px",
                  fontSize: "14px",
                  color: "#666",
                }}
              >
                <strong>Permissions: </strong>
                {getPermissions(form.role)}
              </div>
            </Form.Group>

            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
              >
                Create User
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body>
          <h4 className="mb-3">
            👥 Current Users
          </h4>

          {loading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table
              striped
              bordered
              hover
              responsive
            >
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Permissions</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        {user.username}
                      </td>

                      <td>
                        <Form.Select
                          size="sm"
                          value={user.role}
                          onChange={(e) =>
                            handleUpdate(
                              user.id,
                              {
                                role: e.target.value,
                              }
                            )
                          }
                        >
                          <option value="VA">
                            VA
                          </option>

                          <option value="ITEM_LOOKUP">
                            Item Lookup
                          </option>

                          <option value="LISTER">
                            Lister
                          </option>

                          <option value="MANAGER">
                            Manager
                          </option>

                          <option value="OWNER">
                            Owner
                          </option>
                        </Form.Select>
                      </td>

                      <td>
                        {getPermissions(user.role)}
                      </td>

                      <td>
                        <Button
                          size="sm"
                          variant={
                            user.is_active
                              ? "success"
                              : "secondary"
                          }
                          onClick={() =>
                            handleUpdate(
                              user.id,
                              {
                                is_active:
                                  !user.is_active,
                              }
                            )
                          }
                        >
                          {user.is_active
                            ? "Active"
                            : "Inactive"}
                        </Button>
                      </td>

                      <td>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() =>
                            handleDelete(
                              user.id
                            )
                          }
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center"
                    >
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}